import { ref } from 'vue';
import type { Rule } from 'ant-design-vue/es/form';
import { checkKeyRegistered } from '@/services';
import { validateTelephone } from '@/utils/modules/validate';

export interface UserRegistrationInfo {
  accountName: string;
  companyName: string;
  companyId?: number;
  companyPostalCode: string;
  companyAddress: string;
  companyTelephone: string;
  companyEmail: string;
  contractPic: string;
  offices: {
    officeId: number;
    officeName: string;
    officePostalCode: string;
    officeAddress: string;
    officeTelephone: string;
    officeEmail: string;
    shouldDisabled?: boolean;
  }[];
  wastes: {
    wasteId: number;
    dischargeSitename: string;
    wastePostalCode: string;
    wasteAddress: string;
    wasteAddress2: string;
    wasteAddress3: string;
    wasteEmail: string;
    wasteTelephone: string;
    wasteStaff: string;
    shouldDisabled?: boolean;
  }[];
  registrantNumber: string;
  ediConfirmationKey: string;
}

export interface AccountBoolean {
  accountName: boolean;
  companyName: boolean;
  companyPostalCode: boolean;
  companyAddress: boolean;
  companyTelephone: boolean;
  companyEmail: boolean;
  offices: {
    officeName: boolean;
    officePostalCode: boolean;
    officeAddress: boolean;
    officeTelephone: boolean;
    officeEmail: boolean;
  };
  wastes: {
    dischargeSitename: boolean;
    wastePostalCode: boolean;
    wasteAddress: boolean;
    wasteTelephone: boolean;
    wasteEmail: boolean;
    wasteStaff: boolean;
  };
  registrantNumber: boolean;
  ediConfirmationKey: boolean;
  dischargeSitename: boolean;
}

export const requiredData: AccountBoolean = {
  accountName: true,
  companyName: true,
  companyPostalCode: true,
  companyAddress: true,
  companyTelephone: true,
  companyEmail: true,
  offices: {
    officeName: true,
    officePostalCode: true,
    officeAddress: true,
    officeTelephone: true,
    officeEmail: true
  },
  wastes: {
    dischargeSitename: true,
    wastePostalCode: true,
    wasteAddress: true,
    wasteTelephone: true,
    wasteEmail: true,
    wasteStaff: true
  },
  registrantNumber: true,
  ediConfirmationKey: true,
  dischargeSitename: true
};

export const readonlyData: AccountBoolean = {
  accountName: false,
  companyName: false,
  companyPostalCode: false,
  companyAddress: false,
  companyTelephone: false,
  companyEmail: false,
  offices: {
    officeName: false,
    officePostalCode: false,
    officeAddress: false,
    officeTelephone: false,
    officeEmail: false
  },
  wastes: {
    dischargeSitename: false,
    wastePostalCode: false,
    wasteAddress: false,
    wasteTelephone: false,
    wasteEmail: false,
    wasteStaff: false
  },
  registrantNumber: false,
  ediConfirmationKey: false,
  dischargeSitename: false
};

export const scrollToField = (fieldId: string) => {
  const fieldElement = document.querySelector(`#${fieldId}`) as HTMLElement;
  if (fieldElement) {
    fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    fieldElement.focus();
  }
};

export const createRules = (
  t: (key: string) => string,
  userId: number
): Record<string, Rule[]> => ({
  accountName: [{ required: true, message: t('MSG_00_21'), trigger: ['blur', 'change'] }],
  companyName: [{ required: true, message: t('MSG_00_21'), trigger: ['blur', 'change'] }],
  companyPostalCode: [
    {
      pattern: /^[0-9]{3}-[0-9]{4}$/,
      message: t('MSG_00_23'),
      trigger: ['blur', 'change']
    },
    {
      required: true,
      message: t('MSG_00_21'),
      trigger: ['blur', 'change']
    }
  ],
  companyAddress: [{ required: true, message: t('MSG_00_21'), trigger: ['blur', 'change'] }],
  companyTelephone: [
    {
      validator: (rule: any, value: string) => validateTelephone(t, rule, value),
      trigger: ['blur', 'change']
    }
  ],
  companyEmail: [
    {
      type: 'email',
      message: t('MSG_00_22'),
      trigger: ['blur', 'change']
    },
    {
      required: true,
      message: t('MSG_00_21'),
      trigger: ['blur', 'change']
    }
  ],
  contractPic: [{ required: true, message: t('MSG_00_21'), trigger: ['blur', 'change'] }],

  'offices.officeName': [{ required: true, message: t('MSG_00_21'), trigger: ['blur', 'change'] }],
  'offices.officePostalCode': [
    {
      pattern: /^[0-9]{3}-[0-9]{4}$/,
      message: t('MSG_00_23'),
      trigger: ['blur', 'change']
    },
    {
      required: true,
      message: t('MSG_00_21'),
      trigger: ['blur', 'change']
    }
  ],
  'offices.officeAddress': [{ required: true, message: t('MSG_00_21'), trigger: ['blur', 'change'] }],
  'offices.officeTelephone': [
    {
      validator: (rule: any, value: string) => validateTelephone(t, rule, value),
      trigger: ['blur', 'change']
    }
  ],
  'offices.officeEmail': [
    {
      type: 'email',
      message: t('MSG_00_22'),
      trigger: ['blur', 'change']
    },
    {
      required: true,
      message: t('MSG_00_21'),
      trigger: ['blur', 'change']
    }
  ],
  'wastes.dischargeSitename': [{ required: true, message: t('MSG_00_21'), trigger: ['blur', 'change'] }],
  'wastes.wastePostalCode': [
    {
      pattern: /^[0-9]{3}-[0-9]{4}$/,
      message: t('MSG_00_23'),
      trigger: ['blur', 'change']
    },
    {
      required: true,
      message: t('MSG_00_21'),
      trigger: ['blur', 'change']
    }
  ],
  'wastes.wasteAddress3': [{ required: true, message: t('MSG_00_21'), trigger: ['blur', 'change'] }],
  'wastes.wasteTelephone': [
    {
      validator: (rule: any, value: string) => validateTelephone(t, rule, value),
      trigger: ['blur', 'change']
    }
  ],
  'wastes.wasteEmail': [
    {
      type: 'email',
      message: t('MSG_00_22'),
      trigger: ['blur', 'change']
    },
    {
      required: true,
      message: t('MSG_00_21'),
      trigger: ['blur', 'change']
    }
  ],
  'wastes.wasteStaff': [
    {
      required: true,
      message: t('MSG_00_21'),
      trigger: ['blur', 'change']
    }
  ],
  registrantNumber: [
    {
      validator: (rule, value) => validateRegistrantNumber(rule, value, userId, t),
      trigger: ['blur', 'change']
    }
  ],
  ediConfirmationKey: [
    {
      validator: (rule, value) => validateEdiConfirmationKey(rule, value, userId, t),
      trigger: ['blur', 'change']
    }
  ]
});


const validateRegistrantNumber = async (
  rule: any,
  value: string,
  userId: number,
  t: (key: string) => string
) => {
  const pattern = /^[a-zA-Z0-9]{7}$/; 
  if (!value) {
    return Promise.resolve();
  } else if (!pattern.test(value)) {
    return Promise.reject(new Error(t('MSG_05_2')));
  } else {
    const isExisted = await checkKeyRegistered(value, userId);
    if (isExisted) {
      return Promise.reject(new Error(t('sub_num_can_not_use')));
    }
    return Promise.resolve();
  }
};

const validateEdiConfirmationKey = async (
  rule: any,
  value: string,
  userId: number,
  t: (key: string) => string
) => {
  const pattern = /^[0-9]{8}$/;
  if (!value) {
    return Promise.resolve();
  } else if (!pattern.test(value)) {
    return Promise.reject(new Error(t('MSG_05_3')));
  } else {
    const isExisted = await checkKeyRegistered(value, userId);
    if (isExisted) {
      return Promise.reject(new Error(t('edi_key_can_not_use')));
    }
    return Promise.resolve();
  }
};
